/* Storefront behaviour for the four brands: attribution cart attributes, delivery dates, doona bundles, portrait uploads, LP buy bar. */
(() => {
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const fill = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));
  const PRODUCT_SELECT = 'shopify:product:select';

  /* Attribution: ad click ids and UTM tags as cart attributes for server-side purchase events.
     Sent once per session and value, and only when the visitor allows marketing. */
  function cookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }
  function readStore() {
    try {
      return JSON.parse(sessionStorage.getItem('mb_attr') || '{}');
    } catch {
      return {};
    }
  }
  function sendAttribution() {
    const q = new URLSearchParams(location.search);
    const store = readStore();
    const fbclid = q.get('fbclid') || '';
    const fbc = fbclid ? (store._fbclid === fbclid && store._fbc ? store._fbc : `fb.1.${Date.now()}.${fbclid}`) : cookie('_fbc');
    const found = {
      _fbp: cookie('_fbp'),
      _fbc: fbc,
      _ttp: cookie('_ttp'),
      _ttclid: q.get('ttclid') || '',
      _utm_source: q.get('utm_source') || '',
      _utm_campaign: q.get('utm_campaign') || '',
      _utm_content: q.get('utm_content') || '',
    };
    const send = {};
    for (const [k, v] of Object.entries(found)) if (v && store[k] !== v) send[k] = v.slice(0, 250);
    if (!Object.keys(send).length) return;
    if (!store._landing) send._landing = (location.pathname + location.search).slice(0, 250);
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: send }),
      credentials: 'same-origin',
      keepalive: true,
    })
      .then((r) => {
        if (!r.ok) return;
        Object.assign(store, send, fbclid ? { _fbclid: fbclid } : {});
        try {
          sessionStorage.setItem('mb_attr', JSON.stringify(store));
        } catch {}
      })
      .catch(() => {});
  }
  function withMarketingConsent(fn) {
    const check = () => {
      const cp = window.Shopify && Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') {
        if (cp.marketingAllowed()) fn();
        return true;
      }
      return false;
    };
    document.addEventListener('visitorConsentCollected', () => check());
    if (check()) return;
    if (window.Shopify && typeof Shopify.loadFeatures === 'function') {
      Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
        if (!err) check();
      });
    }
  }

  /* Internal test orders: ?internal=1 sets a first-party cookie for a year (?internal=0 removes it). While it is set the
     cart carries _internal=1 so the fulfilment back ends skip supplier orders and purchase events. Strictly necessary,
     so it does not wait for marketing consent. */
  function cartUpdate(attributes) {
    return fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
      credentials: 'same-origin',
      keepalive: true,
    });
  }
  function syncInternalFlag() {
    const q = new URLSearchParams(location.search).get('internal');
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    if (q === '1') document.cookie = `mb_internal=1; Max-Age=31536000; Path=/; SameSite=Lax${secure}`;
    if (q === '0') document.cookie = `mb_internal=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
    const on = cookie('mb_internal') === '1';
    let state = '';
    try {
      state = sessionStorage.getItem('mb_internal_sent') || '';
    } catch {}
    const remember = (v) => {
      try {
        sessionStorage.setItem('mb_internal_sent', v);
      } catch {}
    };
    if (on) {
      fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => (cart.attributes && cart.attributes._internal === '1' ? null : cartUpdate({ _internal: '1' })))
        .then(() => remember('1'))
        .catch(() => {});
    } else if (state !== '0') {
      fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => (cart.attributes && cart.attributes._internal ? cartUpdate({ _internal: '' }) : null))
        .then(() => remember('0'))
        .catch(() => {});
    }
  }

  /* Delivery window: business days from today, in the brand's locale. */
  function addBusinessDays(date, n) {
    const d = new Date(date);
    let left = n;
    while (left > 0) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) left--;
    }
    return d;
  }
  function initEta(el) {
    const [a, b] = (el.dataset.mbEta || '').split('-').map((x) => parseInt(x, 10));
    if (!a) return;
    const fmt = new Intl.DateTimeFormat(el.dataset.mbLocale || document.documentElement.lang || 'en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const now = new Date();
    const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
    el.innerHTML = fill(el.dataset.mbTemplate, { from: esc(fmt.format(addBusinessDays(now, a))), to: esc(fmt.format(addBusinessDays(now, b || a))) });
  }

  /* Variant picker state shared by the bundle and portrait blocks. */
  const sectionOf = (el) => el.closest('.shopify-section') || document;
  function pickerValues(scope) {
    const values = {};
    scope.querySelectorAll('variant-picker input:checked, variant-picker select').forEach((el) => {
      const opt = el.tagName === 'SELECT' ? el.selectedOptions[0] : el;
      if (opt && opt.dataset.optionName) values[opt.dataset.optionName] = opt.value;
    });
    return values;
  }
  /* Calls fn on picker changes, and again once Horizon has finished updating the section for the new variant. */
  function onVariant(scope, fn) {
    scope.addEventListener('change', (e) => {
      if (e.target.closest && e.target.closest('variant-picker')) requestAnimationFrame(() => fn(e));
    });
    scope.addEventListener(PRODUCT_SELECT, (e) => {
      if (e.target.closest && e.target.closest('product-card')) return;
      Promise.resolve(e.promise)
        .then(() => requestAnimationFrame(() => fn(null)))
        .catch(() => {});
    });
  }

  /* While Horizon fetches a new variant, an add to cart is queued as JSON without line item properties.
     Hold submits of forms that carry properties until the variant update has landed, then submit again. */
  const pending = new WeakMap();
  document.addEventListener(PRODUCT_SELECT, (e) => {
    const section = e.target.closest && e.target.closest('.shopify-section');
    if (!section || e.target.closest('product-card')) return;
    const done = Promise.resolve(e.promise).catch(() => {});
    pending.set(section, done);
    done.then(() => setTimeout(() => pending.get(section) === done && pending.delete(section), 80));
  });
  window.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const hasProps = [...form.elements].some((el) => el.name && el.name.startsWith('properties[') && !el.disabled && el.type !== 'hidden');
      if (!hasProps) return;
      const section = form.closest('.shopify-section');
      const wait = section && pending.get(section);
      if (!wait) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      wait.then(() => setTimeout(() => form.requestSubmit(), 120));
    },
    true
  );

  /* Doona bundles: chosen size details, honest multi-pack value, second colour. */
  function initBundle(el) {
    const scope = sectionOf(el);
    const get = () => {
      const box = scope.querySelector('[data-mb-bundle]');
      const dataEl = box && box.querySelector('[data-mb-bundle-data]');
      return dataEl ? { box, data: JSON.parse(dataEl.textContent) } : null;
    };
    function current(data) {
      const vals = pickerValues(scope);
      return data.variants.find((v) => v.options.every((o, i) => vals[data.names[i]] === undefined || vals[data.names[i]] === o)) || null;
    }
    function update(e) {
      const ctx = get();
      if (!ctx) return;
      const { box, data } = ctx;
      const o = data.options;
      if (!e && o.bundle >= 0 && o.size >= 0) {
        const vals = pickerValues(scope);
        const chosen = data.bundles[vals[data.names[o.bundle]]];
        const fixed = chosen && chosen.fixed ? String(chosen.fixed).toLowerCase() : '';
        const sizeNow = String(vals[data.names[o.size]] || '').toLowerCase();
        if (fixed && sizeNow && sizeNow !== fixed) {
          const want = [...scope.querySelectorAll('variant-picker input')].find((i) => i.dataset.optionName === data.names[o.size] && i.value.toLowerCase() === fixed);
          if (want && !want.checked) {
            want.click();
            return;
          }
        }
      }
      const v = current(data);
      if (!v) return;
      const size = o.size >= 0 ? v.options[o.size] : '';
      const z = data.sizes[size];
      const sizeEl = box.querySelector('[data-mb-size-info]');
      const valueEl = box.querySelector('[data-mb-value]');
      const second = box.querySelector('[data-mb-second]');
      const select = second && second.querySelector('select');
      if (sizeEl) sizeEl.textContent = z ? fill(data.t.size, { size, cm: z.cm, in: z.in, weight: z.weight, fits: z.fits }) : '';
      const multi = v.units > 1;
      if (valueEl) {
        valueEl.hidden = !multi;
        if (multi) valueEl.innerHTML = fill(v.saving ? data.t.value : data.t.plain, { units: String(v.units), size, price: v.price, regular: v.regular || '', saving: v.saving || '' });
      }
      if (second && select) {
        second.hidden = !multi;
        select.disabled = !multi;
        const colourName = o.colour >= 0 ? data.names[o.colour] : '';
        if (multi && e && e.target && e.target.dataset && e.target.dataset.optionName === colourName) select.value = v.options[o.colour];
      }
    }
    onVariant(scope, update);
    update(null);
  }

  /* Portrait personalisation: photo upload with checks and previews, memorial dates, pets hint. */
  const OK_TYPES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif'];
  const OK_EXT = /\.(jpe?g|png|heic|heif)$/i;
  function initPortrait(el) {
    const formId = el.dataset.form;
    const max = parseInt(el.dataset.maxPhotos, 10) || 3;
    const maxBytes = (parseInt(el.dataset.maxMb, 10) || 20) * 1024 * 1024;
    const pick = el.querySelector('[data-mb-pick]');
    const slots = [...el.querySelectorAll('[data-mb-slot]')];
    const list = el.querySelector('[data-mb-previews]');
    const err = el.querySelector('[data-mb-error]');
    const status = el.querySelector('[data-mb-status]');
    const drop = el.querySelector('[data-mb-drop]');
    const scope = sectionOf(el);
    let files = [];
    if (!pick || typeof DataTransfer !== 'function') return;
    pick.removeAttribute('name');
    pick.removeAttribute('required');

    const say = (msg) => {
      if (!status) return;
      status.textContent = '';
      setTimeout(() => (status.textContent = msg), 30);
    };
    const showError = (msg) => {
      err.textContent = msg;
      err.hidden = !msg;
      pick.setAttribute('aria-invalid', msg ? 'true' : 'false');
    };
    function sync() {
      slots.forEach((slot, i) => {
        const dt = new DataTransfer();
        if (files[i]) dt.items.add(files[i]);
        slot.files = dt.files;
        slot.disabled = !files[i];
      });
      pick.setCustomValidity(files.length ? '' : el.dataset.tRequired);
    }
    function render() {
      for (const img of list.querySelectorAll('img')) URL.revokeObjectURL(img.src);
      list.textContent = '';
      files.forEach((f, i) => {
        const li = document.createElement('li');
        li.className = 'mb-upload__item';
        const thumb = document.createElement('div');
        thumb.className = 'mb-upload__thumb';
        if (/image\/(jpeg|png)/.test(f.type)) {
          const img = document.createElement('img');
          img.alt = '';
          img.src = URL.createObjectURL(f);
          thumb.append(img);
        } else {
          thumb.textContent = (f.name.split('.').pop() || 'file').toUpperCase();
        }
        const name = document.createElement('span');
        name.className = 'mb-upload__name';
        name.textContent = f.name;
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'mb-upload__remove';
        rm.setAttribute('aria-label', fill(el.dataset.tRemove, { name: f.name }));
        rm.textContent = '×';
        rm.addEventListener('click', () => {
          files.splice(i, 1);
          render();
          sync();
          say(fill(el.dataset.tRemoved, { name: f.name }));
          pick.focus();
        });
        li.append(thumb, name, rm);
        list.append(li);
      });
    }
    function add(incoming) {
      let msg = '';
      for (const f of incoming) {
        if (!(OK_TYPES.includes(f.type) || OK_EXT.test(f.name))) {
          msg = el.dataset.tType;
          continue;
        }
        if (f.size > maxBytes) {
          msg = el.dataset.tSize;
          continue;
        }
        if (files.length >= max) {
          msg = el.dataset.tCount;
          break;
        }
        if (!files.some((x) => x.name === f.name && x.size === f.size)) files.push(f);
      }
      showError(msg);
      render();
      sync();
      if (files.length && !msg) say(fill(el.dataset.tAdded, { count: String(files.length) }));
    }
    pick.addEventListener('change', () => {
      const chosen = [...pick.files];
      pick.value = '';
      add(chosen);
    });
    pick.addEventListener('invalid', () => {
      showError(el.dataset.tRequired);
      if (drop) drop.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    });
    if (drop) {
      drop.addEventListener('dragover', (e) => {
        e.preventDefault();
        drop.classList.add('is-over');
      });
      drop.addEventListener('dragleave', () => drop.classList.remove('is-over'));
      drop.addEventListener('drop', (e) => {
        e.preventDefault();
        drop.classList.remove('is-over');
        if (e.dataTransfer && e.dataTransfer.files) add([...e.dataTransfer.files]);
      });
    }
    sync();

    const memorialField = el.querySelector('[data-mb-memorial-field]');
    const petsHelp = el.querySelector('[data-mb-pets-help]');
    const styleCards = [...el.querySelectorAll('[data-style-options]')];
    onVariant(scope, () => {
      const vals = Object.values(pickerValues(scope));
      const isMemorial = vals.includes(el.dataset.memorial);
      if (memorialField) {
        memorialField.hidden = !isMemorial;
        memorialField.querySelector('input').disabled = !isMemorial;
      }
      if (petsHelp) petsHelp.textContent = vals.some((v) => /^[23]\b/.test(v)) ? petsHelp.dataset.many : petsHelp.dataset.one;
      for (const card of styleCards) card.hidden = !card.dataset.styleOptions.split('|').some((o) => vals.includes(o));
    });

    window.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== formId) return;
        if (files.length === 0) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showError(el.dataset.tRequired);
          pick.focus({ preventScroll: true });
          return;
        }
        say(el.dataset.tUploading);
      },
      true
    );
  }

  /* Presell pages: buy bar once the reader is past the title, hidden again at the closing call to action. */
  function initLpBar() {
    const bar = document.querySelector('[data-mb-lp-bar]');
    if (!bar || !('IntersectionObserver' in window)) return;
    const top = document.querySelector('.mb-lp .lp-byline, .mb-lp__title');
    const fin = document.querySelector('.mb-lp__final');
    let pastTop = false;
    let finalOn = false;
    const set = () => {
      const on = pastTop && !finalOn;
      bar.classList.toggle('is-on', on);
      bar.inert = !on;
      document.documentElement.style.scrollPaddingBottom = on ? `${bar.offsetHeight + 12}px` : '';
    };
    set();
    if (top) new IntersectionObserver(([e]) => { pastTop = !e.isIntersecting && e.boundingClientRect.top < 0; set(); }).observe(top);
    if (fin) new IntersectionObserver(([e]) => { finalOn = e.isIntersecting; set(); }).observe(fin);
  }

  /* Cookie settings link in brand footers: only shown when Shopify's privacy banner can open its preferences. */
  function initCookieLinks() {
    const links = document.querySelectorAll('[data-mb-cookie-prefs]');
    if (!links.length) return;
    const can = () => window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function';
    const toggle = () => links.forEach((b) => (b.closest('li').hidden = !can()));
    toggle();
    setTimeout(toggle, 3000);
    document.addEventListener('click', (e) => {
      const btn = e.target.closest && e.target.closest('[data-mb-cookie-prefs]');
      if (btn && can()) window.privacyBanner.showPreferences();
    });
  }

  /* Images: one delayed retry when a request fails (e.g. a rate-limited CDN response), instead of leaving the alt text. */
  function retryImage(img) {
    if (!(img instanceof HTMLImageElement) || img.dataset.mbRetry || !img.closest('main, .theme-drawer, cart-drawer-component')) return;
    img.dataset.mbRetry = '1';
    setTimeout(() => {
      const bust = (u) => u + (u.includes('?') ? '&' : '?') + 'mbr=1';
      if (img.srcset) img.srcset = img.srcset.split(',').map((part) => { const [u, d] = part.trim().split(/\s+/); return bust(u) + (d ? ' ' + d : ''); }).join(', ');
      if (img.getAttribute('src')) img.src = bust(img.getAttribute('src'));
    }, 1500);
  }
  document.addEventListener('error', (e) => retryImage(e.target), true);
  ready(() => document.querySelectorAll('main img').forEach((img) => img.complete && img.currentSrc && img.naturalWidth === 0 && retryImage(img)));

  /* House bar: on narrow screens keep the current brand's tab in view. */
  function initHouseBar() {
    const bar = document.querySelector('.mb-house__inner');
    const tab = bar && bar.querySelector('[aria-current]');
    if (!tab || bar.scrollWidth <= bar.clientWidth) return;
    const left = tab.offsetLeft - (bar.clientWidth - tab.offsetWidth) / 2;
    bar.scrollLeft = Math.max(0, left);
  }

  ready(() => {
    initHouseBar();
    syncInternalFlag();
    setTimeout(() => withMarketingConsent(sendAttribution), 1500);
    document.querySelectorAll('[data-mb-eta]').forEach(initEta);
    document.querySelectorAll('[data-mb-bundle]').forEach(initBundle);
    document.querySelectorAll('[data-mb-portrait]').forEach(initPortrait);
    initLpBar();
    initCookieLinks();
  });
})();
